// Ensure this file is recognized as a module
export {};

import React, { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';

interface ThreePlaneProps {
  className?: string;
}

const ThreePlane: React.FC<ThreePlaneProps> = ({ className = '' }) => {
  const mountRef = useRef<HTMLDivElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  // Move character variables to component scope using useRef
  const characterSpritesRef = useRef<THREE.Mesh[]>([]);
  const characterJumpStatesRef = useRef<{ isJumping: boolean; jumpStartTime: number; jumpHeight: number }[]>([]);

  useEffect(() => {
    if (!mountRef.current) return;

    // Store ref value to avoid cleanup warning
    const mountElement = mountRef.current;

    // Scene setup
    const scene = new THREE.Scene();
    
    // Camera setup
    const camera = new THREE.PerspectiveCamera(
      45, // Wider field of view to span full width
      mountElement.clientWidth / mountElement.clientHeight,
      0.1,
      1000
    );
    camera.position.set(0, 8, 25); // Position camera closer for more zoomed in view
    camera.lookAt(0, -2, 0); // Look at the ground level

    // Renderer setup with transparent background
    const renderer = new THREE.WebGLRenderer({ 
      alpha: true,
      antialias: true,
      preserveDrawingBuffer: true
    });
    renderer.setSize(mountElement.clientWidth, mountElement.clientHeight);
    renderer.setClearColor(0x000000, 0); // Transparent background
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    
    mountElement.appendChild(renderer.domElement);

    // Create ground plane geometry (X-Z axis)
    const planeGeometry = new THREE.PlaneGeometry(20, 20, 32, 32);
    
    // Create pixel-art gravel road texture
    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 64;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Base color
    ctx.fillStyle = '#29304e';
    ctx.fillRect(0, 0, 64, 64);
    
    // Add subtle gravel texture with pixel art style
    for (let i = 0; i < 80; i++) {
      const x = Math.floor(Math.random() * 64);
      const y = Math.floor(Math.random() * 64);
      const size = Math.random() * 2 + 1;
      
      // More subtle gravel colors
      const colors = ['#2a3148', '#3a415e', '#4a516e'];
      ctx.fillStyle = colors[Math.floor(Math.random() * colors.length)];
      
      // Draw subtle pixel-art gravel pieces
      ctx.fillRect(x, y, size, size);
      
      // Add some very subtle darker spots
      if (Math.random() > 0.8) {
        ctx.fillStyle = '#1a1f2e';
        ctx.fillRect(x, y, size, size);
      }
    }
    
    // Add very subtle road lines/patterns
    ctx.fillStyle = '#3a415e';
    for (let i = 0; i < 64; i += 12) {
      ctx.fillRect(i, 0, 1, 64);
    }
    
    // Create texture from canvas
    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(4, 4); // Repeat the texture
    
    // Create material with texture
    const planeMaterial = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      opacity: 1.0,
      side: THREE.DoubleSide
    });

    // Create plane mesh
    const plane = new THREE.Mesh(planeGeometry, planeMaterial);
    plane.rotation.x = -Math.PI / 2; // Rotate to X-Z plane (ground)
    plane.position.y = -15; // Move plane down further
    plane.renderOrder = 0; // Render first
    plane.receiveShadow = false; // No longer need shadow receiving
    scene.add(plane);
    
    // Add toon-shaded rock assets
    const createGradientMap = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 256;
      canvas.height = 1;
      const ctx = canvas.getContext('2d');
      if (!ctx) return new THREE.CanvasTexture(new OffscreenCanvas(256, 1));
      const gradient = ctx.createLinearGradient(0, 0, 256, 0);
      gradient.addColorStop(0, '#000000');
      gradient.addColorStop(0.3, '#333333');
      gradient.addColorStop(0.7, '#666666');
      gradient.addColorStop(1, '#ffffff');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, 256, 1);
      return new THREE.CanvasTexture(canvas);
    };
    
    const rockGeometry = new THREE.DodecahedronGeometry(0.3, 0);
    const rockMaterial = new THREE.MeshToonMaterial({
      color: 0x4a516e,
      transparent: true,
      opacity: 0.9,
      gradientMap: createGradientMap()
    });
    
    // Create many smaller rocks scattered around
    for (let i = 0; i < 25; i++) {
      const rock = new THREE.Mesh(rockGeometry, rockMaterial);
      rock.position.set(
        (Math.random() - 0.5) * 18, // X position (slightly wider spread)
        -14.5, // Y position (slightly below plane)
        (Math.random() - 0.5) * 10 + 5  // Z position (always positive, behind billboard)
      );
      rock.rotation.set(
        Math.random() * Math.PI,
        Math.random() * Math.PI,
        Math.random() * Math.PI
      );
      rock.scale.set(
        Math.random() * 0.3 + 0.2, // Smaller scale range
        Math.random() * 0.3 + 0.2,
        Math.random() * 0.3 + 0.2
      );
      scene.add(rock);
    }
    
    // Enhanced Cloud System with Custom Toon Shader
    const createCloudToonShader = () => {
      return {
        uniforms: {
          time: { value: 0 },
          lightPosition: { value: new THREE.Vector3(10, 10, 10) },
          lightColor: { value: new THREE.Color(0xffffff) },
          toonLevels: { value: 4.0 },
          rimPower: { value: 2.0 },
          rimIntensity: { value: 0.22 }
        },
        vertexShader: `
          varying vec3 vNormal;
          varying vec3 vPosition;
          varying vec3 vViewPosition;
          uniform float time;
          
          void main() {
            vNormal = normalize(normalMatrix * normal);
            vPosition = position;
            
            // Add subtle cloud movement
            vec3 pos = position;
            pos.x += sin(time * 0.1 + position.y * 2.0) * 0.02;
            pos.y += cos(time * 0.15 + position.x * 1.5) * 0.01;
            
            vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
            vViewPosition = -mvPosition.xyz;
            gl_Position = projectionMatrix * mvPosition;
          }
        `,
        fragmentShader: `
          varying vec3 vNormal;
          varying vec3 vPosition;
          varying vec3 vViewPosition;
          uniform vec3 lightPosition;
          uniform vec3 lightColor;
          uniform float toonLevels;
          uniform float rimPower;
          uniform float rimIntensity;
          uniform float time;
          
          void main() {
            vec3 normal = normalize(vNormal);
            vec3 viewDir = normalize(vViewPosition);
            vec3 lightDir = normalize(lightPosition - vPosition);
            
            float diff = max(dot(normal, lightDir), 0.0);
            float toonDiff = floor(diff * toonLevels) / toonLevels;
            
            float rim = 1.0 - max(dot(viewDir, normal), 0.0);
            rim = pow(rim, rimPower);
            
            vec3 color;
            if (toonDiff < 0.25) {
              color = mix(vec3(0.65, 0.75, 0.95), vec3(0.5, 0.7, 1.0), toonDiff * 4.0);
            } else if (toonDiff < 0.5) {
              color = mix(vec3(0.75, 0.85, 0.98), vec3(0.88, 0.93, 1.0), (toonDiff - 0.25) * 4.0);
            } else if (toonDiff < 0.75) {
              color = mix(vec3(0.88, 0.93, 1.0), vec3(0.95, 0.97, 1.0), (toonDiff - 0.5) * 4.0);
            } else {
              color = vec3(0.95, 0.97, 1.0);
            }
            color += rim * rimIntensity * vec3(1.0);
            gl_FragColor = vec4(color, 0.8);
          }
        `
      };
    };

    // Create cloud geometry and material
    const cloudMaterial = new THREE.ShaderMaterial(createCloudToonShader());
    
    // Create cloud groups with multiple spheres (restored previous implementation)
    const cloudGroups: THREE.Group[] = [];
    for (let i = 0; i < 20; i++) {
      const cloudGroup = new THREE.Group();
      const baseX = (Math.random() - 0.5) * 40;
      const baseY = -2 + Math.random() * 8; // Lower Y position range
      const baseZ = -20 + Math.random() * 20; // Bring clouds closer to visible area
      
      // Create more complex cloud shapes with multiple geometries
      const cloudSpheres = 4 + Math.floor(Math.random() * 4);
      for (let j = 0; j < cloudSpheres; j++) {
        const radius = 0.8 + Math.random() * 1.2;
        const segments = 12 + Math.floor(Math.random() * 8);
        const cloudGeometry = new THREE.SphereGeometry(radius, segments, segments);
        const cloud = new THREE.Mesh(cloudGeometry, cloudMaterial);
        cloud.castShadow = false;
        cloud.receiveShadow = false;
        cloud.position.set(
          (Math.random() - 0.5) * 2.5,
          (Math.random() - 0.5) * 1.8,
          (Math.random() - 0.5) * 2.5
        );
        const scale = 0.4 + Math.random() * 0.6;
        cloud.scale.set(scale, scale * 0.7, scale);
        cloudGroup.add(cloud);
      }
      cloudGroup.position.set(baseX, baseY, baseZ);
      cloudGroup.rotation.set(
        Math.random() * Math.PI * 0.5,
        Math.random() * Math.PI * 0.5,
        Math.random() * Math.PI * 0.3
      );
      scene.add(cloudGroup);
      cloudGroups.push(cloudGroup);
    }

    // Add lighting
    const ambientLight = new THREE.AmbientLight(0x87ceeb, 0.6); // Sky blue ambient
    scene.add(ambientLight);
    
    const sunLight = new THREE.DirectionalLight(0xffffff, 0.8);
    sunLight.position.set(10, 20, 10);
    sunLight.castShadow = true;
    sunLight.shadow.mapSize.width = 2048;
    sunLight.shadow.mapSize.height = 2048;
    sunLight.shadow.camera.near = 0.5;
    sunLight.shadow.camera.far = 50;
    sunLight.shadow.camera.left = -20;
    sunLight.shadow.camera.right = 20;
    sunLight.shadow.camera.top = 20;
    sunLight.shadow.camera.bottom = -20;
    scene.add(sunLight);

    // Add fill light for softer shadows
    const fillLight = new THREE.DirectionalLight(0x87ceeb, 0.3);
    fillLight.position.set(-10, 10, -10);
    scene.add(fillLight);
    
    // Add cloud fill light
    const cloudFillLight = new THREE.DirectionalLight(0xffffff, 0.4);
    cloudFillLight.position.set(0, 30, 0);
    scene.add(cloudFillLight);
    
    // Create a separate scene for the billboard to avoid any lighting interference
    const billboardScene = new THREE.Scene();

    // Create character sprites that move around the ground
    const characterSprites: THREE.Mesh[] = [];
    const characterDirections: number[] = []; // 1 for right, -1 for left
    const characterTargets: THREE.Vector3[] = [];
    const characterJumpStates: { isJumping: boolean; jumpStartTime: number; jumpHeight: number }[] = [];
    
    // Configurable movement bounds
    const CHARACTER_BOUNDS = {
      X_MIN: -7.5,
      X_MAX: 7.5,
      Z_MIN: -10,
      Z_MAX: 2,
      Y_POSITION: -14.5
    };
    
    // Load character textures
    const char1LeftTexture = new THREE.TextureLoader().load('/char1_left.png');
    const char1RightTexture = new THREE.TextureLoader().load('/char1_right.png');
    const char2LeftTexture = new THREE.TextureLoader().load('/char2_left.png');
    const char2RightTexture = new THREE.TextureLoader().load('/char2_right.png');
    
    // Configure textures
    [char1LeftTexture, char1RightTexture, char2LeftTexture, char2RightTexture].forEach(texture => {
      texture.minFilter = THREE.NearestFilter;
      texture.magFilter = THREE.NearestFilter;
      texture.generateMipmaps = false;
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.flipY = true;
    });
    
    // Create character 1
    const char1Geometry = new THREE.PlaneGeometry(1, 1);
    const char1Material = new THREE.MeshBasicMaterial({
      map: char1RightTexture,
      transparent: true,
      side: THREE.FrontSide,
      depthTest: false,
      depthWrite: false,
      fog: false,
      alphaTest: 0.1
    });
    
    const char1 = new THREE.Mesh(char1Geometry, char1Material);
    char1.position.set(-5, CHARACTER_BOUNDS.Y_POSITION, 0); // Start position on ground, more forward
    char1.scale.set(2, 2, 1); // Scale up the character
    char1.renderOrder = 1000; // Render after billboard to be in front
    billboardScene.add(char1);
    characterSprites.push(char1);
    characterDirections.push(1); // Start facing right
    characterTargets.push(new THREE.Vector3(5, CHARACTER_BOUNDS.Y_POSITION, -2)); // Initial target
    characterJumpStates.push({ isJumping: false, jumpStartTime: 0, jumpHeight: 0 });
    
    // Create character 2
    const char2Geometry = new THREE.PlaneGeometry(1, 1);
    const char2Material = new THREE.MeshBasicMaterial({
      map: char2LeftTexture,
      transparent: true,
      side: THREE.FrontSide,
      depthTest: false,
      depthWrite: false,
      fog: false,
      alphaTest: 0.1
    });
    
    const char2 = new THREE.Mesh(char2Geometry, char2Material);
    char2.position.set(3, CHARACTER_BOUNDS.Y_POSITION, -1); // Start position on ground, more forward
    char2.scale.set(2, 2, 1); // Scale up the character
    char2.renderOrder = 1000; // Render after billboard to be in front
    billboardScene.add(char2);
    characterSprites.push(char2);
    characterDirections.push(-1);
    characterTargets.push(new THREE.Vector3(-3, CHARACTER_BOUNDS.Y_POSITION, 1)); // Initial target
    characterJumpStates.push({ isJumping: false, jumpStartTime: 0, jumpHeight: 0 });

    // Create character shadows
    const characterShadows: THREE.Mesh[] = [];
    
    // Create shadow material for char1 using flipped sprite
    const char1ShadowMaterial = new THREE.MeshBasicMaterial({
      map: char1RightTexture,
      color: 0x000000, // Black shadow color
      transparent: true,
      opacity: 0.4,
      side: THREE.FrontSide,
      depthTest: true,
      depthWrite: false
    });
    
    const char1Shadow = new THREE.Mesh(char1Geometry, char1ShadowMaterial);
    char1Shadow.position.set(-5, CHARACTER_BOUNDS.Y_POSITION + 0.05, 0); // Higher above ground
    char1Shadow.scale.set(-3, 1, 1); // More tilted projection - wider and flatter
    char1Shadow.rotation.x = -Math.PI / 2; // Lay flat on ground
    char1Shadow.renderOrder = 5; // Render after ground but before characters
    char1Shadow.frustumCulled = false; // Disable frustum culling
    scene.add(char1Shadow); // Add to main scene for proper ground positioning
    characterShadows.push(char1Shadow);
    
    // Create shadow material for char2 using flipped sprite
    const char2ShadowMaterial = new THREE.MeshBasicMaterial({
      map: char2LeftTexture,
      color: 0x000000, // Black shadow color
      transparent: true,
      opacity: 0.4,
      side: THREE.FrontSide,
      depthTest: true,
      depthWrite: false
    });
    
    const char2Shadow = new THREE.Mesh(char2Geometry, char2ShadowMaterial);
    char2Shadow.position.set(3, CHARACTER_BOUNDS.Y_POSITION + 0.05, -1); // Higher above ground
    char2Shadow.scale.set(-3, 1, 1); // More tilted projection - wider and flatter
    char2Shadow.rotation.x = -Math.PI / 2; // Lay flat on ground
    char2Shadow.renderOrder = 5; // Render after ground but before characters
    char2Shadow.frustumCulled = false; // Disable frustum culling
    scene.add(char2Shadow); // Add to main scene for proper ground positioning
    characterShadows.push(char2Shadow);
    
    // Create cherry blossom particle system
    const createCherryBlossomParticles = () => {
      const particleCount = 75;
      const particles = new THREE.BufferGeometry();
      const positions = new Float32Array(particleCount * 3);
      const velocities = new Float32Array(particleCount * 3);
      const sizes = new Float32Array(particleCount);
      const colors = new Float32Array(particleCount * 3);
      
      // Initialize particles
      for (let i = 0; i < particleCount; i++) {
        const i3 = i * 3;
        
        // Random positions across the scene
        positions[i3] = (Math.random() - 0.5) * 40; // X spread
        positions[i3 + 1] = Math.random() * 10 - 75; // Y height (lower spawn range)
        positions[i3 + 2] = (Math.random() - 0.5) * 20 + -50; // Z spread
        
        // Random velocities for floating motion
        velocities[i3] = (Math.random() - 0.5) * 0.02; // X drift
        velocities[i3 + 1] = (Math.random() - 0.5) * 0.01; // Y drift
        velocities[i3 + 2] = (Math.random() - 0.5) * 0.02; // Z drift
        
        // Random sizes for variety (larger particles)
        sizes[i] = Math.random() * 0.5 + 0.5;
        
        // Cherry blossom colors (pink to white)
        const pinkIntensity = Math.random() * 0.8 + 0.2;
        colors[i3] = 1.0; // R
        colors[i3 + 1] = 0.8 * pinkIntensity + 0.2; // G (pink tint)
        colors[i3 + 2] = 0.9 * pinkIntensity + 0.1; // B (pink tint)
      }
      
      particles.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      particles.setAttribute('velocity', new THREE.BufferAttribute(velocities, 3));
      particles.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
      particles.setAttribute('color', new THREE.BufferAttribute(colors, 3));
      
      // Create particle material
      const particleMaterial = new THREE.PointsMaterial({
        size: 0.8,
        map: createCherryBlossomTexture(),
        transparent: true,
        opacity: 0.9,
        vertexColors: true,
        blending: THREE.NormalBlending,
        depthWrite: false,
        depthTest: false,
        sizeAttenuation: true
      });
      
      const particleSystem = new THREE.Points(particles, particleMaterial);
      particleSystem.userData = { velocities, positions };
      
      return particleSystem;
    };
    
    // Create cherry blossom texture
    const createCherryBlossomTexture = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 32;
      canvas.height = 32;
      const ctx = canvas.getContext('2d');
      if (!ctx) return new THREE.CanvasTexture(new OffscreenCanvas(32, 32));
      
      // Create soft circular particle
      const gradient = ctx.createRadialGradient(16, 16, 0, 16, 16, 16);
      gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
      gradient.addColorStop(0.7, 'rgba(255, 200, 220, 0.8)');
      gradient.addColorStop(1, 'rgba(255, 200, 220, 0)');
      
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, 32, 32);
      
      const texture = new THREE.CanvasTexture(canvas);
      texture.minFilter = THREE.LinearFilter;
      texture.magFilter = THREE.LinearFilter;
      return texture;
    };
    
    // Add particle system to billboard scene so they render after the billboard
    const cherryBlossoms = createCherryBlossomParticles();
    cherryBlossoms.renderOrder = 1000; // Render on top of everything
    billboardScene.add(cherryBlossoms);
    
    // Create billboarding sprite for buildings
    const textureLoader = new THREE.TextureLoader();
    textureLoader.load('/stage_foreground.png', (texture) => {
      // Disable any texture filtering that might cause white edges
      texture.minFilter = THREE.NearestFilter;
      texture.magFilter = THREE.NearestFilter;
      texture.generateMipmaps = false;
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.flipY = true; // Flip texture to correct orientation
      
      // Create a plane geometry for the billboard instead of sprite
      const billboardGeometry = new THREE.PlaneGeometry(1, 1);
      const billboardMaterial = new THREE.MeshBasicMaterial({ 
        map: texture,
        transparent: true,
        side: THREE.FrontSide,
        depthTest: false,
        depthWrite: false,
        fog: false,
        alphaTest: 0.1,
        color: 0xffffff // Force white color to prevent tinting
      });
      
      const billboard = new THREE.Mesh(billboardGeometry, billboardMaterial);
      billboard.position.set(0, -11, -18); // Position above the plane
      billboard.renderOrder = 999; // Render last
      billboard.castShadow = false;
      billboard.receiveShadow = false;
      billboard.frustumCulled = false; // Disable frustum culling
      
      // Calculate aspect ratio to prevent stretching
      const aspectRatio = texture.image.width / texture.image.height;
      const baseSize = 28;
      billboard.scale.set(baseSize * aspectRatio, baseSize, 1);
      
      // Make it always face the camera
      billboard.lookAt(camera.position);
      
      billboardScene.add(billboard);
    });

    // Animation variables
    let animationId: number;
    let time = 0;
    
    // Update refs with the character data
    characterSpritesRef.current = characterSprites;
    characterJumpStatesRef.current = characterJumpStates;
    
    // Jump function
    const makeCharacterJump = () => {
      // Select a random character to jump
      const randomIndex = Math.floor(Math.random() * characterSpritesRef.current.length);
      const jumpState = characterJumpStatesRef.current[randomIndex];
      
      if (!jumpState.isJumping) {
        jumpState.isJumping = true;
        jumpState.jumpStartTime = Date.now();
        jumpState.jumpHeight = Math.random() * 3 + 2; // Random jump height between 2-5 units
        console.log(`Character ${randomIndex + 1} is jumping!`);
      }
    };
    
    // Spacebar event listener
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.code === 'Space') {
        event.preventDefault(); // Prevent page scroll
        makeCharacterJump();
        
        // Dispatch custom event for score system
        const jumpEvent = new CustomEvent('characterJump', {
          detail: { timestamp: Date.now() }
        });
        window.dispatchEvent(jumpEvent);
      }
    };
    
    // Add event listener
    window.addEventListener('keydown', handleKeyDown);
    
    // Animation loop
    const animate = () => {
      animationId = requestAnimationFrame(animate);
      time += 0.016; // Approximately 60fps
      
      // Update cloud shader uniforms
      cloudGroups.forEach(group => {
        group.children.forEach(child => {
          if (child instanceof THREE.Mesh && child.material instanceof THREE.ShaderMaterial) {
            child.material.uniforms.time.value = time;
          }
        });
      });
      
      // Animate cloud groups
      cloudGroups.forEach((group, index) => {
        // Slow floating motion
        group.position.y += Math.sin(time * 0.2 + index) * 0.001;
        group.position.x += Math.cos(time * 0.15 + index * 0.5) * 0.002;
        
        // Subtle rotation
        group.rotation.y += 0.001;
      });
      
      // Animate character movement
      characterSprites.forEach((character, index) => {
        const target = characterTargets[index];
        const currentPos = character.position;
        const direction = characterDirections[index];
        const jumpState = characterJumpStates[index];
        
        // Handle jump animation
        if (jumpState.isJumping) {
          const jumpTime = (Date.now() - jumpState.jumpStartTime) / 1000; // Convert to seconds
          const jumpDuration = 1.0; // 1 second jump
          
          if (jumpTime < jumpDuration) {
            // Calculate jump arc (parabolic motion)
            const jumpProgress = jumpTime / jumpDuration;
            const jumpArc = Math.sin(jumpProgress * Math.PI); // Creates a smooth arc
            const jumpY = CHARACTER_BOUNDS.Y_POSITION + (jumpState.jumpHeight * jumpArc);
            currentPos.y = jumpY;
          } else {
            // Jump finished, return to ground
            currentPos.y = CHARACTER_BOUNDS.Y_POSITION;
            jumpState.isJumping = false;
          }
        }
        
        // Move towards target
        const moveSpeed = 0.02;
        const directionVector = new THREE.Vector3();
        directionVector.subVectors(target, currentPos).normalize();
        currentPos.add(directionVector.multiplyScalar(moveSpeed));
        
        // Check if reached target (within 0.5 units)
        if (currentPos.distanceTo(target) < 0.5) {
          // Generate new random target within bounds
          const newX = CHARACTER_BOUNDS.X_MIN + Math.random() * (CHARACTER_BOUNDS.X_MAX - CHARACTER_BOUNDS.X_MIN);
          const newZ = CHARACTER_BOUNDS.Z_MIN + Math.random() * (CHARACTER_BOUNDS.Z_MAX - CHARACTER_BOUNDS.Z_MIN);
          target.set(newX, CHARACTER_BOUNDS.Y_POSITION, newZ);
          
          // Update direction based on movement
          const newDirection = newX > currentPos.x ? 1 : -1;
          if (newDirection !== direction) {
            characterDirections[index] = newDirection;
            
            // Change sprite texture based on direction
            if (index === 0) { // char1
              (character.material as THREE.MeshBasicMaterial).map = 
                newDirection === 1 ? char1RightTexture : char1LeftTexture;
            } else { // char2
              (character.material as THREE.MeshBasicMaterial).map = 
                newDirection === 1 ? char2RightTexture : char2LeftTexture;
            }
          }
        }
        
        // Make character always face the camera
        character.lookAt(camera.position);
      });
      
      // Animate character shadows to follow characters
      characterShadows.forEach((shadow, index) => {
        const character = characterSprites[index];
        const direction = characterDirections[index];
        const jumpState = characterJumpStates[index];
        
        // Update shadow position to match character (but keep Y position fixed)
        shadow.position.x = character.position.x;
        shadow.position.z = character.position.z + 0.5; // Move shadow closer to character
        shadow.position.y = CHARACTER_BOUNDS.Y_POSITION + 0.05; // Keep fixed Y position
        
        // Handle shadow animation during jumps
        if (jumpState.isJumping) {
          const jumpTime = (Date.now() - jumpState.jumpStartTime) / 1000;
          const jumpDuration = 1.0;
          
          if (jumpTime < jumpDuration) {
            const jumpProgress = jumpTime / jumpDuration;
            const jumpArc = Math.sin(jumpProgress * Math.PI);
            
            // Scale shadow based on jump height (smaller when character is higher)
            const shadowScale = 1.0 - (jumpArc * 0.3); // Shadow gets 30% smaller at peak
            shadow.scale.set(-3 * shadowScale, shadowScale, shadowScale);
            
            // Reduce shadow opacity during jump
            const shadowOpacity = 0.4 - (jumpArc * 0.2); // Shadow fades during jump
            (shadow.material as THREE.MeshBasicMaterial).opacity = Math.max(0.1, shadowOpacity);
          } else {
            // Reset shadow when jump is finished
            shadow.scale.set(-3, 1, 1);
            (shadow.material as THREE.MeshBasicMaterial).opacity = 0.4;
          }
        } else {
          // Normal shadow state
          shadow.scale.set(-3, 1, 1);
          (shadow.material as THREE.MeshBasicMaterial).opacity = 0.4;
        }
        
        // Update shadow texture to match character direction
        if (index === 0) { // char1 shadow
          (shadow.material as THREE.MeshBasicMaterial).map = 
            direction === 1 ? char1LeftTexture : char1RightTexture; // Use opposite texture to match direction
        } else { // char2 shadow
          (shadow.material as THREE.MeshBasicMaterial).map = 
            direction === 1 ? char2LeftTexture : char2RightTexture; // Use opposite texture to match direction
        }
      });
      
      // Animate cherry blossom particles
      if (cherryBlossoms && cherryBlossoms.userData.velocities) {
        const positions = cherryBlossoms.geometry.attributes.position.array as Float32Array;
        const velocities = cherryBlossoms.userData.velocities as Float32Array;
        
        for (let i = 0; i < positions.length; i += 3) {
          // Update positions based on velocities
          positions[i] += velocities[i];
          positions[i + 1] += velocities[i + 1];
          positions[i + 2] += velocities[i + 2];
          
          // Add subtle wind effect
          positions[i] += Math.sin(time * 0.5 + i * 0.1) * 0.001;
          positions[i + 1] += Math.cos(time * 0.3 + i * 0.05) * 0.0005;
          
          // Wrap particles around scene boundaries
          if (positions[i] > 20) positions[i] = -20;
          if (positions[i] < -20) positions[i] = 20;
          if (positions[i + 1] > 3) positions[i + 1] = -5; // Lower Y boundary
          if (positions[i + 1] < -5) positions[i + 1] = 3; // Upper Y boundary
          if (positions[i + 2] > 0) positions[i + 2] = -20;
          if (positions[i + 2] < -20) positions[i + 2] = 0;
        }
        
        cherryBlossoms.geometry.attributes.position.needsUpdate = true;
      }
      
      // Render scenes
      renderer.autoClear = false;
      renderer.setClearColor(0x000000, 0);
      renderer.clear();
      renderer.render(scene, camera);
      renderer.render(billboardScene, camera);
    };
    
    // Debug: log plane position and camera position
    console.log('Plane position:', plane.position);
    console.log('Camera position:', camera.position);
    console.log('Plane visible:', plane.visible);
    console.log('Sun light position:', sunLight.position);
    
    // Log cloud positions to verify they're in the right area
    cloudGroups.forEach((group, index) => {
      console.log(`Cloud ${index} position:`, group.position);
    });

    // Start animation
    animate();
    
    // Debug: log that scene is rendered
    console.log('Three.js scene rendered successfully');

    // Handle resize
    const handleResize = () => {
      if (!mountElement) return;
      
      const width = mountElement.clientWidth;
      const height = mountElement.clientHeight;
      
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      
      renderer.setSize(width, height);
    };

    window.addEventListener('resize', handleResize);
    
    setIsLoaded(true);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('keydown', handleKeyDown);
      cancelAnimationFrame(animationId);
      if (mountElement) {
        mountElement.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, []);

  return (
    <div 
      ref={mountRef} 
      className={`w-full h-full ${className}`}
      style={{ minHeight: '600px' }}
    >
      {!isLoaded && (
        <div className="flex items-center justify-center h-full">
          <div className="text-white text-lg">Loading 3D Scene...</div>
        </div>
      )}
    </div>
  );
};

export default ThreePlane; 